import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  Form,
  Input,
  Progress,
  Space,
  Typography,
  theme,
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthCard } from "../components/AuthCard";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_RULES,
  isPasswordAcceptable,
  passwordRulesMet,
} from "../common/constants";
import { AUTH_ERROR_CODE } from "../common/types";
import { usePreferences } from "../hooks/usePreferences";
import { getAuthErrorCode, resetPassword } from "../lib/http";
import { useNotifier } from "../providers/NotificationProvider";

const REDIRECT_SECONDS = 3;

function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const notifier = useNotifier();
  const { token: themeToken } = theme.useToken();
  const { direction } = usePreferences();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [expired, setExpired] = useState(false);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [password, setPassword] = useState("");

  // Capture the token once, then take it out of the URL and the history entry so
  // it does not linger in the address bar, in bookmarks, or in a shared screen.
  const [resetToken] = useState(() => searchParams.get("token") ?? "");
  useEffect(() => {
    if (searchParams.get("token")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-redirect countdown once the password has been changed.
  useEffect(() => {
    if (!done) {
      return;
    }
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    navigate("/");
  }, [done, countdown, navigate]);

  const rulesMet = passwordRulesMet(password);
  const strengthPercent = Math.round((rulesMet / PASSWORD_RULES.length) * 100);
  const strengthColor =
    rulesMet === PASSWORD_RULES.length
      ? themeToken.colorSuccess
      : rulesMet >= 3
        ? themeToken.colorWarning
        : themeToken.colorError;

  const onFinish = async (values: { password: string }) => {
    setLoading(true);
    try {
      await resetPassword({ token: resetToken, password: values.password });
      setDone(true);
    } catch (error) {
      const code = getAuthErrorCode(error);
      if (code === AUTH_ERROR_CODE.INVALID_TOKEN) {
        setExpired(true);
      } else {
        notifier.error({
          message: t("auth.resetPassword.error"),
          description:
            code === AUTH_ERROR_CODE.WEAK_PASSWORD
              ? t("auth.resetPassword.errorWeakPassword")
              : t("auth.resetPassword.errorDescription"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // A link that is missing, malformed, already used, or expired all land here:
  // the only useful next step is requesting a fresh one.
  if (!resetToken || expired) {
    return (
      <AuthCard
        title={t("auth.resetPassword.invalidTitle")}
        subtitle={t("auth.resetPassword.invalidSubtitle")}
      >
        <Flex vertical gap={16} align="center">
          <CloseCircleOutlined
            style={{ fontSize: 48, color: themeToken.colorError }}
          />
          <Button
            type="primary"
            block
            size="large"
            onClick={() => navigate("/forgot-password")}
          >
            {t("auth.resetPassword.requestNewLink")}
          </Button>
          <Button type="link" block onClick={() => navigate("/")}>
            {t("auth.logout.backToLogin")}
          </Button>
        </Flex>
      </AuthCard>
    );
  }

  if (done) {
    return (
      <AuthCard
        title={t("auth.resetPassword.doneTitle")}
        subtitle={t("auth.resetPassword.doneSubtitle")}
      >
        <Flex vertical gap={16} align="center">
          <CheckCircleOutlined
            style={{ fontSize: 48, color: themeToken.colorSuccess }}
          />
          <Typography.Text type="secondary">
            {t("auth.logout.redirecting", { seconds: countdown })}
          </Typography.Text>
          <Button
            type="primary"
            block
            size="large"
            onClick={() => navigate("/")}
          >
            {t("auth.logout.backToLogin")}
          </Button>
        </Flex>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t("auth.resetPassword.title")}
      subtitle={t("auth.resetPassword.subtitle")}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        size="large"
        onFinish={onFinish}
      >
        <Form.Item
          label={t("auth.resetPassword.password")}
          name="password"
          rules={[
            {
              required: true,
              message: t("auth.resetPassword.passwordRequired"),
            },
            {
              validator: (_, value) =>
                !value || isPasswordAcceptable(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(t("auth.resetPassword.passwordPolicy")),
                    ),
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t("auth.resetPassword.passwordPlaceholder")}
            autoComplete="new-password"
            maxLength={PASSWORD_MAX_LENGTH}
            dir={direction === "rtl" ? "rtl" : "ltr"}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Form.Item>

        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Progress
            percent={strengthPercent}
            showInfo={false}
            strokeColor={strengthColor}
            size="small"
            aria-label={t("auth.resetPassword.strength")}
          />
          <Space direction="vertical" size={0}>
            {PASSWORD_RULES.map((rule) => {
              const met = rule.test.test(password);
              return (
                <Typography.Text
                  key={rule.key}
                  type={met ? "success" : "secondary"}
                  style={{ fontSize: themeToken.fontSizeSM }}
                >
                  <CheckCircleFilled
                    style={{
                      marginInlineEnd: 8,
                      color: met
                        ? themeToken.colorSuccess
                        : themeToken.colorTextQuaternary,
                    }}
                  />
                  {t(`auth.resetPassword.rule.${rule.key}`)}
                </Typography.Text>
              );
            })}
          </Space>
        </Space>

        <Form.Item
          label={t("auth.resetPassword.confirmPassword")}
          name="confirmPassword"
          dependencies={["password"]}
          style={{ marginTop: 24 }}
          rules={[
            {
              required: true,
              message: t("auth.resetPassword.confirmRequired"),
            },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || getFieldValue("password") === value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(t("auth.resetPassword.confirmMismatch")),
                    ),
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t("auth.resetPassword.confirmPlaceholder")}
            autoComplete="new-password"
            maxLength={PASSWORD_MAX_LENGTH}
            dir={direction === "rtl" ? "rtl" : "ltr"}
          />
        </Form.Item>

        <Button
          loading={loading}
          disabled={loading}
          type="primary"
          htmlType="submit"
          block
          size="large"
          style={{ marginTop: 8 }}
        >
          {t("auth.resetPassword.submit")}
        </Button>
      </Form>
    </AuthCard>
  );
}

export default ResetPassword;
